import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { LogoAvatar } from "@/components/LogoUploader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Building2, MapPin, Phone, Mail, Check, X, Search, ChevronLeft, ChevronRight } from "lucide-react";

const PAGE_SIZE = 12;

function ApprovalCard({ item, kind, onApprove, onReject }) {
  return (
    <div className="bg-white rounded-3xl border border-[#2563EB]/10 shadow-sm p-6" data-testid={`${kind}-item-${item.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <LogoAvatar
            logo={item.logo_base64}
            size="md"
            fallbackClass={kind === "schools" ? "bg-[#DBEAFE] text-[#2563EB]" : "bg-[#FFF3E0] text-[#E65100]"}
          />
          <div>
            <h3 className="font-heading text-lg font-semibold text-foreground">{item.name}</h3>
            <p className="text-xs text-brand-inkMuted">
              {kind === "branches" ? `of ${item.school_name} · ` : ""}
              {new Date(item.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        <StatusBadge status={item.status} />
      </div>
      <div className="text-sm text-brand-inkMuted space-y-1">
        {item.address && <p className="flex items-start gap-1.5"><MapPin className="w-3.5 h-3.5 mt-0.5" />{item.address}</p>}
        {item.contact_email && <p className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{item.contact_email}</p>}
        {item.contact_phone && <p className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{item.contact_phone}</p>}
        {item.principal_name && <p>Principal: {item.principal_name}</p>}
        {item.description && <p className="mt-2 italic">{item.description}</p>}
      </div>
      {item.status === "pending" && (
        <div className="mt-4 flex gap-2">
          <Button onClick={() => onApprove(kind, item.id)}
            className="rounded-full bg-[#81C784] hover:bg-[#66BB6A] text-white"
            data-testid={`approve-${kind === "schools" ? "school" : "branch"}-${item.id}`}>
            <Check className="w-4 h-4 mr-1" /> Approve
          </Button>
          <Button onClick={() => onReject({ kind, item })}
            variant="outline"
            className="rounded-full border-[#F06292] text-[#C2185B] hover:bg-[#FFEBEE]"
            data-testid={`reject-${kind === "schools" ? "school" : "branch"}-${item.id}`}>
            <X className="w-4 h-4 mr-1" /> Reject
          </Button>
        </div>
      )}
      {item.status === "rejected" && item.rejection_reason && (
        <div className="mt-3 rounded-xl bg-[#FFEBEE] text-[#C62828] px-3 py-2 text-xs">
          Reason: {item.rejection_reason}
        </div>
      )}
    </div>
  );
}

export default function AdminApprovalsPage() {
  const [tab, setTab] = useState("schools");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [filter, setFilter] = useState("pending");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  // Debounce search
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(id);
  }, [q]);

  useEffect(() => { setPage(1); }, [filter, debouncedQ, tab]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page, page_size: PAGE_SIZE,
        status_filter: filter,
        ...(debouncedQ ? { q: debouncedQ } : {}),
      };
      const { data: res } = await api.get(`/admin/${tab}`, { params });
      setData(res || { items: [], total: 0, total_pages: 1 });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [filter, page, debouncedQ, tab]);

  useEffect(() => { load(); }, [load]);

  const approve = async (kind, id) => {
    try {
      await api.post(`/${kind}/${id}/approve`);
      toast.success(`${kind === "schools" ? "School" : "Branch"} approved 🎉`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reject = async () => {
    if (!rejectTarget) return;
    try {
      await api.post(`/${rejectTarget.kind}/${rejectTarget.item.id}/reject`, { reason: rejectReason });
      toast.success("Marked as rejected");
      setRejectTarget(null);
      setRejectReason("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const goto = (p) => {
    if (p < 1 || p > (data.total_pages || 1)) return;
    setPage(p);
  };

  return (
    <>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground tracking-tight">Pending Approval</h1>
          <p className="text-brand-inkMuted mt-1">Review and approve schools and branches.</p>
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-inkMuted" />
          <Input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, address…"
            className="rounded-2xl h-11 pl-10 border-[#2563EB]/20"
            data-testid="approvals-search-input"
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="filter-pills">
        {["pending", "approved", "rejected", "all"].map((k) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold capitalize transition ${
              filter === k
                ? "bg-[#2563EB] text-white shadow-sm"
                : "bg-white text-foreground border border-[#2563EB]/20 hover:bg-[#EFF6FF]"
            }`}
            data-testid={`filter-${k}`}
          >
            {k}
          </button>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="bg-[#DBEAFE] rounded-full p-1 h-auto">
          <TabsTrigger value="schools" className="rounded-full px-5 py-2 data-[state=active]:bg-white data-[state=active]:text-[#1D4ED8]" data-testid="tab-schools">
            Schools{tab === "schools" ? ` (${data.total})` : ""}
          </TabsTrigger>
          <TabsTrigger value="branches" className="rounded-full px-5 py-2 data-[state=active]:bg-white data-[state=active]:text-[#1D4ED8]" data-testid="tab-branches">
            Branches{tab === "branches" ? ` (${data.total})` : ""}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="schools" className="mt-6">
          {renderApprovalGrid({ tab: "schools", loading, data, approve, setRejectTarget })}
          {renderPagination({ page, total_pages: data.total_pages, total: data.total, goto })}
        </TabsContent>

        <TabsContent value="branches" className="mt-6">
          {renderApprovalGrid({ tab: "branches", loading, data, approve, setRejectTarget })}
          {renderPagination({ page, total_pages: data.total_pages, total: data.total, goto })}
        </TabsContent>
      </Tabs>

      <Dialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <DialogContent className="rounded-3xl border-[#2563EB]/20" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              Reject {rejectTarget?.kind === "schools" ? "school" : "branch"}?
            </DialogTitle>
            <DialogDescription>
              Share a reason — it'll be visible to the school owner so they can iterate.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="e.g. Need more information about the campus address."
            className="rounded-2xl border-[#2563EB]/20"
            rows={4}
            data-testid="reject-reason-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)} className="rounded-full border-[#2563EB]/30">
              Cancel
            </Button>
            <Button onClick={reject} className="rounded-full bg-[#C62828] hover:bg-[#B71C1C] text-white" data-testid="confirm-reject-btn">
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function renderApprovalGrid({ tab, loading, data, approve, setRejectTarget }) {
  if (loading) {
    return <div className="text-brand-inkMuted py-12 text-center font-heading">Loading...</div>;
  }
  if (!data.items.length) {
    return (
      <div className="bg-white rounded-3xl border border-[#2563EB]/10 p-10 text-center text-brand-inkMuted" data-testid={`no-${tab}-admin`}>
        No {tab} in this view.
      </div>
    );
  }
  return (
    <div className="grid md:grid-cols-2 gap-5">
      {data.items.map((it) => (
        <ApprovalCard key={it.id} item={it} kind={tab} onApprove={approve} onReject={setRejectTarget} />
      ))}
    </div>
  );
}

function renderPagination({ page, total_pages, total, goto }) {
  if (!total) return null;
  return (
    <div className="mt-6 flex items-center justify-between flex-wrap gap-3" data-testid="pagination-bar">
      <p className="text-sm text-brand-inkMuted">
        Showing <span className="font-heading font-semibold text-foreground">{Math.min(total, (page - 1) * PAGE_SIZE + 1)}–{Math.min(total, page * PAGE_SIZE)}</span> of <span className="font-heading font-semibold text-foreground">{total}</span>
      </p>
      <div className="flex items-center gap-1">
        <Button
          variant="outline" size="sm"
          onClick={() => goto(page - 1)}
          disabled={page <= 1}
          className="rounded-full border-[#2563EB]/30 text-[#2563EB] hover:bg-[#EFF6FF] disabled:opacity-40"
          data-testid="pagination-prev"
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Prev
        </Button>
        <span className="px-3 py-1 text-sm font-heading text-brand-inkMuted">
          Page <span className="text-foreground font-semibold">{page}</span> of <span className="text-foreground font-semibold">{total_pages}</span>
        </span>
        <Button
          variant="outline" size="sm"
          onClick={() => goto(page + 1)}
          disabled={page >= total_pages}
          className="rounded-full border-[#2563EB]/30 text-[#2563EB] hover:bg-[#EFF6FF] disabled:opacity-40"
          data-testid="pagination-next"
        >
          Next <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}
